import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import Label from "@components/form/Label";
import TextInput from "@components/form/TextInput";
import { useAppContext } from "@context/AppContext";
import { updateCurrentUser } from "@services/user";
import { strongPassword } from "@utils/strongPassword";
import FormDialog from "./FormDialog";

const ChangePasswordDialog: FunctionComponent<{
  onClose: () => void;
  onChanged: () => void;
}> = ({ onClose, onChanged }) => {
  const { t } = useTranslation();
  const {
    loggedInInfo: { userDetails },
  } = useAppContext();
  const schema = z
    .object({
      password: z
        .string()
        .regex(
          strongPassword,
          t("auth.new_password_validation_message") as string,
        ),
      passwordConfirmation: z.string(),
    })
    .refine(data => data.password === data.passwordConfirmation, {
      message: t("common.required.confirm_password") as string,
      path: ["passwordConfirmation"],
    });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
  });
  const { mutate, isLoading, isError } = useMutation(updateCurrentUser, {
    onSuccess: onChanged,
  });

  return (
    <FormDialog
      title={t("auth.change_password")}
      submitLabel={t("common.save")}
      onClose={onClose}
      // PUT /users/me requires the unchanged details alongside the password.
      onSubmit={handleSubmit(({ password }) =>
        mutate({
          name: userDetails?.name,
          email: userDetails?.email,
          tel: userDetails?.tel,
          password,
        }),
      )}
      busy={isLoading}
      error={isError && t("profile.saved_failure")}
    >
      <div>
        <Label htmlFor="new-password">{t("profile.password_field")}</Label>
        <TextInput
          id="new-password"
          type="password"
          autoComplete="new-password"
          {...register("password")}
          errorMessage={errors.password?.message}
        />
      </div>
      <div>
        <Label htmlFor="confirm-password">
          {t("profile.confirm_password_field")}
        </Label>
        <TextInput
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          {...register("passwordConfirmation")}
          errorMessage={errors.passwordConfirmation?.message}
        />
      </div>
    </FormDialog>
  );
};

export default ChangePasswordDialog;
